var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, DateRangePicker, Divider, DropdownButton, FlexBox, Icon, Text, Tooltip, } from '@dixa/lib-shared-habla';
import isEqual from 'lodash/isEqual';
import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import { batch } from 'react-redux';
import { useDebounce, useMap, useUpdateEffect } from 'react-use';
import { analyticsApi, } from 'apis/analytics';
import { AnalyticsBusinessHours } from './components/businessHours/analyticsBusinessHours';
import { useHasAnalyticsBusinessHours } from './components/businessHours/utils/useHasAnalyticsBusinessHours';
import { formatAnalyticsPeriod } from './utils';
/**
 * Type guard that checks whether a draft period is valid and can be propagated
 * to the main state.
 */
const isValidDraft = (draft) => {
    return draft.values.every((item) => item !== null);
};
/** Type guard that checks whether a draft period is a preset. */
const isPreset = (values) => {
    return values.length === 1;
};
export const AnalyticsPeriod = ({ period, settings, settings: { timezone }, onChangePeriod, onChangeSettings, }) => {
    const [draft, { set: patchDraft, setAll: setDraft }] = useMap({
        period,
        settings,
    });
    const { presets, isLoading } = analyticsApi.useReadPeriodPresetsQuery({ timezone }, {
        selectFromResult: (_a) => {
            var _b;
            var { data } = _a, rest = __rest(_a, ["data"]);
            return (Object.assign(Object.assign({}, rest), { presets: (_b = data === null || data === void 0 ? void 0 : data.data.presets) !== null && _b !== void 0 ? _b : [] }));
        },
    });
    const hasBusinessHours = useHasAnalyticsBusinessHours();
    // Updates the draft period when the main state changes.
    useUpdateEffect(() => {
        const shouldUpdate = !isEqual(draft.period, period) || !isEqual(draft.settings, settings);
        if (shouldUpdate) {
            setDraft({ period, settings });
        }
    }, [period, settings]);
    // Updates the period only if the draft is different and is a valid date range. If the draft is a
    // preset, the period is updated immediately, otherwise the period is updated after a delay.
    useDebounce(() => {
        batch(() => {
            if (!isEqual(period, draft.period) && isValidDraft(draft.period)) {
                onChangePeriod(draft.period);
            }
            if (!isEqual(settings, draft.settings)) {
                onChangeSettings(draft.settings);
            }
        });
    }, isPreset(draft.period.values) ? 0 : 1000, [draft]);
    /** The date range value passed down to the datepicker component. */
    const value = useMemo(() => {
        var _a, _b, _c, _d;
        let startDate = null;
        let endDate = null;
        if (isPreset(draft.period.values)) {
            const [preset] = draft.period.values;
            const presetValue = preset.value;
            const presetData = presets.find(({ value }) => value === presetValue);
            startDate = (_a = presetData === null || presetData === void 0 ? void 0 : presetData.start) !== null && _a !== void 0 ? _a : null;
            endDate = (_b = presetData === null || presetData === void 0 ? void 0 : presetData.end) !== null && _b !== void 0 ? _b : null;
        }
        else {
            const [start, end] = draft.period.values;
            startDate = (_c = start === null || start === void 0 ? void 0 : start.value) !== null && _c !== void 0 ? _c : null;
            endDate = (_d = end === null || end === void 0 ? void 0 : end.value) !== null && _d !== void 0 ? _d : null;
        }
        return {
            startDate: startDate ? moment(startDate).tz(timezone) : null,
            endDate: endDate ? moment(endDate).tz(timezone) : null,
        };
    }, [draft, presets, timezone]);
    /** Updates the draft period with the new date range value. */
    const handleChange = (_a) => {
        var _b, _c, _d, _e;
        var { presetKey } = _a, dateRangePickerValue = __rest(_a, ["presetKey"]);
        if (presetKey) {
            setDraft({
                period: {
                    field: 'created_at',
                    values: [
                        {
                            value: presetKey !== null && presetKey !== void 0 ? presetKey : '',
                        },
                    ],
                },
                settings: Object.assign(Object.assign({}, settings), { timezone: (_b = dateRangePickerValue.timezone) !== null && _b !== void 0 ? _b : moment.tz.guess() }),
            });
        }
        else {
            const startDate = (_c = dateRangePickerValue.startDate) === null || _c === void 0 ? void 0 : _c.clone().startOf('day');
            const endDate = (_d = dateRangePickerValue.endDate) === null || _d === void 0 ? void 0 : _d.clone().endOf('day');
            const startDateValue = startDate
                ? {
                    value: startDate.toISOString(),
                }
                : null;
            const endDateValue = endDate
                ? {
                    value: endDate.toISOString(),
                }
                : null;
            setDraft({
                period: { field: 'created_at', values: [startDateValue, endDateValue] },
                settings: Object.assign(Object.assign({}, settings), { timezone: (_e = dateRangePickerValue.timezone) !== null && _e !== void 0 ? _e : moment.tz.guess() }),
            });
        }
    };
    /** Updates the draft settings when a schedule is selected. */
    const handleChangeSchedule = useCallback((schedule) => {
        var _a;
        patchDraft('settings', Object.assign(Object.assign({}, settings), { timezone: (_a = schedule === null || schedule === void 0 ? void 0 : schedule.timezone) !== null && _a !== void 0 ? _a : moment.tz.guess(), schedule_id: schedule === null || schedule === void 0 ? void 0 : schedule.id }));
    }, [patchDraft, settings]);
    /**
     * The text displayed in the button, derived from the original period, not the
     * draft period.
     */
    const buttonText = useMemo(() => formatAnalyticsPeriod(period, timezone, presets), [period, presets, timezone]);
    const dateRangeMaxDate = moment().tz(timezone).add(1, 'day');
    const dateRangePresets = useMemo(() => {
        return presets.map(({ value, label, start, end }) => ({
            label,
            key: value,
            startDate: moment.tz(start, timezone),
            endDate: moment.tz(end, timezone),
        }));
    }, [presets, timezone]);
    const hasSchedule = !!settings.schedule_id;
    return (_jsx(DropdownButton, Object.assign({ icon: "calendar", buttonProps: {
            variant: 'secondary',
            isLoading,
            label: timezone !== moment.tz.guess() ? timezone : undefined,
            styleText: false,
        }, dropdown: _jsxs(FlexBox, Object.assign({ flexDirection: "column", width: "min-content" }, { children: [_jsx(DateRangePicker, { startDate: value.startDate, endDate: value.endDate, maxDate: dateRangeMaxDate, onChange: handleChange, inputsPlacement: "top", presets: dateRangePresets, presetKey: isPreset(period.values) ? period.values[0].value : undefined, timezone: timezone, withTimezoneSelector: true, timezoneSelectorProps: {
                        disabled: hasSchedule,
                        tooltipProps: hasSchedule
                            ? {
                                content: 'The timezone of the selected business hours schedule is applied.',
                                disabled: false,
                            }
                            : undefined,
                    } }), hasBusinessHours && (_jsxs(_Fragment, { children: [_jsx(Divider, { size: "xSmall" }), _jsx(Box, Object.assign({ padding: "300", flex: 1 }, { children: _jsx(AnalyticsBusinessHours, { scheduleId: settings.schedule_id, onChangeSchedule: handleChangeSchedule }) }))] }))] })), fixed: true }, { children: _jsxs(FlexBox, Object.assign({ alignItems: "center", gap: "200" }, { children: [_jsx(Text.Paragraph, Object.assign({ variant: "3", wrap: "nowrap", color: "inherit" }, { children: buttonText })), settings.schedule_id && (_jsx(Tooltip, Object.assign({ content: "Business hours schedule applied", wrap: "div", wrapProps: { style: { display: 'flex' } }, dark: true }, { children: _jsx(Icon, { name: "clock-three" }) })))] })) })));
};
